import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

export const AMBER = '#FAAB1C', BLUE = '#21A84A', NAVY = '#0C2417', NAVY2 = '#12301F';
export const YELLOW = '#FFDB01', GREEN = '#21A84A', GREEN_DEEP = '#15803D';
export const IMG = {
  heroHome: '/landing-heroHome.jpeg',
  heroRoof: '/landing-heroRoof.jpeg',
  aerial: 'https://images.pexels.com/photos/9875685/pexels-photo-9875685.jpeg?auto=compress&cs=tinysrgb&w=1600',
  aerialHouse: 'https://images.unsplash.com/photo-1677938438353-3df2b797568a?fm=jpg&q=80&w=1200',
  suburb: 'https://images.pexels.com/photos/9875679/pexels-photo-9875679.jpeg?auto=compress&cs=tinysrgb&w=1200',
  couple: 'https://images.pexels.com/photos/7579046/pexels-photo-7579046.jpeg?auto=compress&cs=tinysrgb&w=1200',
  family: 'https://images.unsplash.com/photo-1770871821382-60ea99b0a941?fm=jpg&q=80&w=1200',
  installer: 'https://images.pexels.com/photos/9875441/pexels-photo-9875441.jpeg?auto=compress&cs=tinysrgb&w=1200',
  crew: 'https://images.pexels.com/photos/8853502/pexels-photo-8853502.jpeg?auto=compress&cs=tinysrgb&w=1200',
  hands: 'https://images.pexels.com/photos/8853537/pexels-photo-8853537.jpeg?auto=compress&cs=tinysrgb&w=1200',
  pool: 'https://images.pexels.com/photos/2850347/pexels-photo-2850347.jpeg?auto=compress&cs=tinysrgb&w=1200',
  panels: 'https://images.pexels.com/photos/356036/pexels-photo-356036.jpeg?auto=compress&cs=tinysrgb&w=1600',
};

export function Reveal({ children, className = '', delay = 0, as: Tag = 'div', style = {} }) {
  const ref = useRef(); const [on, setOn] = useState(false);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setOn(true); io.disconnect(); } }, { threshold: 0.15 });
    io.observe(el); return () => io.disconnect();
  }, []);
  return <Tag ref={ref} className={`reveal ${on ? 'reveal-on' : ''} ${className}`} style={{ transitionDelay: `${delay}ms`, ...style }}>{children}</Tag>;
}

export function Overline({ children, color = AMBER, className = '' }) {
  return <p className={`text-[11px] font-bold tracking-[0.25em] ${className}`} style={{ color }}>{children}</p>;
}

export function CountUp({ to, prefix = '', suffix = '', decimals = 0, duration = 1400 }) {
  const ref = useRef(); const [v, setV] = useState(0);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const io = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting) return; io.disconnect();
      const t0 = performance.now();
      const step = (t) => { const p = Math.min(1, (t - t0) / duration); setV(to * (1 - Math.pow(1 - p, 3))); if (p < 1) requestAnimationFrame(step); };
      requestAnimationFrame(step);
    }, { threshold: 0.4 });
    io.observe(el); return () => io.disconnect();
  }, [to, duration]);
  return <span ref={ref}>{prefix}{v.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}{suffix}</span>;
}

export function Marquee() {
  const items = ['25-YEAR PANEL WARRANTY', '10-YEAR WORKMANSHIP', 'ROOF-LEAK GUARANTEE', 'PRODUCTION GUARANTEE', '30% FEDERAL TAX CREDIT', 'NJ SREC-II', 'NY-SUN INCENTIVES', 'PA NET METERING', 'CT RESIDENTIAL RENEWABLE', 'TRANSFERABLE TO NEW OWNER'];
  return (
    <div className="overflow-hidden py-4 border-y border-white/10" style={{ background: NAVY2 }} data-testid="guarantee-marquee">
      <div className="marquee-track flex gap-10 whitespace-nowrap">
        {[...items, ...items].map((t, i) => <span key={i} className="flex items-center gap-3 text-xs font-bold tracking-[0.2em] text-slate-300"><span className="w-1.5 h-1.5 rounded-full" style={{ background: i % 2 ? BLUE : AMBER }} />{t}</span>)}
      </div>
    </div>
  );
}

export function Nav() {
  const [solid, setSolid] = useState(false);
  useEffect(() => { const f = () => setSolid(window.scrollY > 40); f(); window.addEventListener('scroll', f); return () => window.removeEventListener('scroll', f); }, []);
  return (
    <nav className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${solid ? 'backdrop-blur-xl border-b border-white/10' : ''}`} style={{ background: solid ? 'rgba(12,36,23,0.82)' : 'transparent' }} data-testid="landing-nav">
      <div className="h-1" style={{ background: `linear-gradient(90deg,${AMBER} 62%,${BLUE} 62%)` }} />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-3.5 flex items-center justify-between">
        <a href="#top" aria-label="Samaritan Solar"><img src="/samaritan-logo.png" alt="Samaritan Solar" className="h-8" /></a>
        <div className="hidden lg:flex items-center gap-7 text-sm text-slate-300">
          {[['#vision', 'AI Roof Design'], ['#savings', '25-Yr Savings'], ['#why', 'Why Samaritan'], ['#process', 'Process'], ['#incentives', 'Incentives'], ['#reviews', 'Reviews']].map(([h, l]) => <a key={h} href={h} className="hover:text-white transition-colors">{l}</a>)}
          <Link to="/careers" className="hover:text-white transition-colors" data-testid="landing-careers-link">Careers</Link>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/auth" className="text-sm font-semibold px-4 py-2 rounded-full border border-white/20 hover:bg-white/10 transition-colors whitespace-nowrap" data-testid="landing-signin-btn">Team sign in</Link>
          <div className="hidden sm:block"><a href="#quote" className="btn-amber text-sm px-5 py-2" data-testid="landing-cta-btn">Get my proposal</a></div>
        </div>
      </div>
    </nav>
  );
}

export function Footer() {
  return (
    <footer className="px-6 py-14 border-t border-white/10" style={{ background: NAVY }} data-testid="landing-footer">
      <div className="max-w-7xl mx-auto grid md:grid-cols-[1.4fr_1fr_1fr] gap-10">
        <div><img src="/samaritan-logo.png" alt="Samaritan Solar" className="h-9 mb-4" /><p className="text-slate-300">Clean power. Honest people.</p><p className="text-slate-500 text-sm mt-2 max-w-sm">Engineering, procurement and construction for homeowners across New York, New Jersey, Connecticut and Pennsylvania. samaritansolar.com</p></div>
        <div><p className="text-[10px] font-bold tracking-widest text-slate-500 mb-3">EXPLORE</p><div className="flex flex-col gap-2 text-sm text-slate-300"><a href="#vision" className="hover:text-white">AI roof design</a><a href="#savings" className="hover:text-white">25-year savings</a><a href="#incentives" className="hover:text-white">Incentives</a><Link to="/careers" className="hover:text-white">Careers</Link></div></div>
        <div><p className="text-[10px] font-bold tracking-widest text-slate-500 mb-3">TEAM</p><div className="flex flex-col gap-2 text-sm text-slate-300"><Link to="/auth" className="hover:text-white">Team sign in</Link><a href="#quote" className="hover:text-white">Request a proposal</a></div></div>
      </div>
      <p className="max-w-7xl mx-auto text-[11px] text-slate-600 mt-10">© {new Date().getFullYear()} Samaritan Solar. Savings figures are illustrative until your personalized proposal is generated on your actual roof. Satellite imagery © Google.</p>
    </footer>
  );
}
